import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface GoogleAdsAccount {
  customer_id: string;
  descriptive_name: string | null;
  timezone: string | null;
  currency_code: string | null;
  is_manager: boolean;
}

export interface GoogleAdsConversionSetup {
  lead_action_resource: string;
  sale_action_resource: string;
  auto_tagging_enabled: boolean;
  ecl_enabled: boolean;
  conversion_tracking_id: string | null;
  account_timezone: string | null;
  account_currency: string | null;
  setup_at: string;
  checked_at: string;
}

export interface GoogleAdsProviderMetadata {
  customer_id: string | null;
  descriptive_name: string | null;
  timezone: string | null;
  currency_code: string | null;
  is_manager: boolean;
  login_customer_id: string | null;
  available_accounts: GoogleAdsAccount[];
  /** Conversion-upload setup written by google-ads-setup; null until it has run. */
  conversion_setup: GoogleAdsConversionSetup | null;
  last_upload_at: string | null;
  last_upload_error: string | null;
  last_verified_at: string | null;
}

export interface GoogleAdsIntegration {
  id: string;
  status: 'connected' | 'expired' | 'revoked' | 'error';
  status_message: string | null;
  connected_at: string;
  last_refreshed_at: string | null;
  last_error: string | null;
  last_error_at: string | null;
  access_token_expires_at: string | null;
  token_valid: boolean;
  metadata: GoogleAdsProviderMetadata;
}

const QUERY_KEY = ['integration-status', 'google_ads'] as const;

export function useGoogleAdsConnection() {
  const queryClient = useQueryClient();

  const statusQuery = useQuery<GoogleAdsIntegration | null>({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_company_integration_status', {
        p_provider: 'google_ads',
      });
      if (error) throw error;
      const row = (data ?? [])[0];
      if (!row) return null;
      const metadata = (row.provider_metadata ?? {}) as Partial<GoogleAdsProviderMetadata>;
      return {
        id: row.id,
        status: row.status as GoogleAdsIntegration['status'],
        status_message: row.status_message,
        connected_at: row.connected_at,
        last_refreshed_at: row.last_refreshed_at,
        last_error: row.last_error,
        last_error_at: row.last_error_at,
        access_token_expires_at: row.access_token_expires_at,
        token_valid: Boolean(row.token_valid),
        metadata: {
          customer_id: metadata.customer_id ?? null,
          descriptive_name: metadata.descriptive_name ?? null,
          timezone: metadata.timezone ?? null,
          currency_code: metadata.currency_code ?? null,
          is_manager: Boolean(metadata.is_manager),
          login_customer_id: metadata.login_customer_id ?? null,
          available_accounts: metadata.available_accounts ?? [],
          conversion_setup: metadata.conversion_setup ?? null,
          last_upload_at: metadata.last_upload_at ?? null,
          last_upload_error: metadata.last_upload_error ?? null,
          last_verified_at: metadata.last_verified_at ?? null,
        },
      };
    },
  });

  // Initiate the OAuth flow. On success the browser is redirected to Google;
  // the user returns via /auth/google-ads/callback (a SPA route) which
  // forwards the auth code to google-ads-oauth-callback and then bounces them
  // back to /settings/integrations with the connection status query string.
  const connectMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('google-ads-oauth-initiate', {
        body: { redirect_origin: window.location.origin },
      });
      if (error) throw new Error(error.message ?? 'Failed to start Google Ads connection');
      const url = (data as { url?: string })?.url;
      if (!url) throw new Error('Server did not return an authorization URL');
      window.location.href = url;
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('google-ads-disconnect', { body: {} });
      if (error) throw new Error(error.message ?? 'Failed to disconnect');
      return data as { ok: boolean; revoked_at_google: boolean };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  const setActiveAccountMutation = useMutation({
    mutationFn: async (customerId: string) => {
      const { data, error } = await supabase.functions.invoke('google-ads-finalize-connect', {
        body: { customer_id: customerId },
      });
      if (error) throw new Error(error.message ?? 'Failed to save account selection');
      return data as {
        ok: boolean;
        customer_id: string;
        descriptive_name: string | null;
        timezone: string | null;
        currency_code: string | null;
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  return {
    integration: statusQuery.data ?? null,
    isLoading: statusQuery.isLoading,
    refetch: statusQuery.refetch,
    connect: connectMutation.mutateAsync,
    isConnecting: connectMutation.isPending,
    disconnect: disconnectMutation.mutateAsync,
    isDisconnecting: disconnectMutation.isPending,
    setActiveAccount: setActiveAccountMutation.mutateAsync,
    isSettingAccount: setActiveAccountMutation.isPending,
  };
}
